import { useAuth } from "@/contexts/auth-context";
import { formatPhoneNumber } from "@/lib/utils";
import icEdit from "@/assets/icons/ic-pen.svg";
import icEmail from "@/assets/icons/ic-my-email.svg";
import icPhone from "@/assets/icons/ic-my-phone.svg";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";

interface ProfileSectionProps {
  onEditClick: () => void;
  onLogoutClick: () => void;
}

export function ProfileSection({ onEditClick, onLogoutClick }: ProfileSectionProps) {
  const { user } = useAuth();
  if (!user) return null;

  const formatJoinDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
  };

  const statusConfig =
    user.status === "active"
      ? { label: "Active", bg: "bg-[#2BAA50]" }
      : { label: "Pending", bg: "bg-[#8D8D8D]" };

  return (
    <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center w-full  lg:border lg:border-[rgba(0,0,0,0.15)] bg-white rounded-[4px]  lg:p-6 gap-6 lg:gap-0">
      <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start w-full">
        {/* Left: User info */}
        <div className="flex flex-col gap-3 flex-1 items-start w-full">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full justify-between items-start ">
            <div className="md:max-w-[742px]">
              {/* Name + Badge */}
              <div className="flex items-center gap-3">
                <span className=" whitespace-pre-line break-all text-[30px] lg:text-[32px] leading-[36px] lg:leading-[40px] tracking-[-0.0213em] lg:tracking-[-0.03em] text-[#000000] font-normal whitespace-pre-line">
                  {user.requester_name || user.email}
                </span>
                {/* <span
                  className={`inline-flex items-center h-6 px-2 text-sm lg:h-8 lg:px-3 rounded-full ${statusConfig.bg} text-white lg:text-lg lg:leading-6 tracking-[-0.01em] capitalize`}
                >
                  {statusConfig.label}
                </span> */}
              </div>

              {/* Organization info with vertical separators */}
              <div className="flex flex-wrap justify-start items-center gap-2 lg:gap-3 text-sm lg:text-base leading-[24px] tracking-[-0.01em] text-[#000000]">
                {user.company_name && <span>{user.company_name}</span>}
                {user.department && (
                  <>
                    <span className="w-0 h-[11px] border-r border-[rgba(0, 0, 0, 0.15)]" />
                    <span>{user.department}</span>
                  </>
                )}
                {user.position && (
                  <>
                    <span className="w-0 h-[11px] border-r border-[rgba(0, 0, 0, 0.15)]" />
                    <span>{user.position}</span>
                  </>
                )}
                {user.job_title && (
                  <>
                    <span className="w-0 h-[11px] border-r border-[rgba(0, 0, 0, 0.15)]" />
                    <span>{user.job_title}</span>
                  </>
                )}
              </div>
            </div>

            {/* Right: Buttons */}
            <div className="lg:flex items-center gap-3 w-full hidden lg:w-[306px] lg:mt-0 ">
              <button
                type="button"
                onClick={onLogoutClick}
                className="flex-1 shrink-0 inline-flex items-center justify-center gap-2 h-12 rounded-[4px] bg-[#EBEBEB] backdrop-blur-[20px] text-base font-bold tracking-[-0.01em] text-[#000000] transition-colors"
              >
                로그아웃
              </button>
              <button
                type="button"
                onClick={onEditClick}
                className="flex-1 shrink-0 inline-flex items-center justify-center gap-2 h-12 rounded-[4px] bg-[#FFAA72] text-base font-bold tracking-[-0.01em] text-black transition-colors"
              >
                <img src={icEdit} alt="Edit" className="w-6 h-6" />
                수정
              </button>
            </div>
          </div>

          {/* Info row: join date, phone, email */}
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-12 w-full lg:mt-0 lg:items-center">
            {user.created_at && (
              <div className="flex items-center justify-start gap-2 min-w-[157px]">
                <span className="w-3 h-3 rounded bg-[#43946C]" />
                <span className="text-sm lg:text-base leading-6 tracking-[-0.01em] text-[#000000]">
                  가입일 {formatJoinDate(user.created_at)}
                </span>
              </div>
            )}
            {user.phone && (
              <div className="flex items-center justify-start gap-2">
                <img src={icPhone} alt="Phone" />
                <span className="text-sm lg:text-base leading-6 tracking-[-0.01em] text-[#000000]">
                  {formatPhoneNumber(user.phone)}
                </span>
              </div>
            )}
            {user.email && (
              <div className="flex items-center justify-start gap-2">
                <img src={icEmail} alt="Email" />
                <TruncatedTooltip className="text-sm lg:text-base leading-6 tracking-[-0.01em] text-[#000000] truncate">
                  {user.email}
                </TruncatedTooltip>
              </div>
            )}
          </div>
        </div>
        {/* Right: Buttons */}
        <div className="flex items-center gap-3 w-full lg:hidden">
          <button
            type="button"
            onClick={onLogoutClick}
            className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-[4px] bg-[#EBEBEB] backdrop-blur-[20px] text-sm font-bold leading-[24px] tracking-[-0.01em] text-[#000000] transition-colors"
          >
            로그아웃
          </button>
          <button
            type="button"
            onClick={onEditClick}
            className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-[4px] bg-[#FFAA72] text-sm font-bold leading-[24px] tracking-[-0.01em] text-black transition-colors"
          >
            <img src={icEdit} alt="Edit" className="w-5 h-5" />
            수정
          </button>
        </div>
      </div>
    </div>
  );
}
